import { useEffect, useRef, useState } from 'react';
import type { MediaItem } from '../../lib/mediaLibrary';
import type { VideoCaption } from '../../lib/videoCaption';
import { drawVideoCaption, hasCaptionText } from '../../lib/videoCaption';
import type { SocialCropState } from './SocialImageCropEditor';

/** A cover the user picked. Absent means the network uses the first frame. */
export type VideoCoverChoice = {
  mode: 'frame' | 'photo';
  /** Seconds into the video. Kept when switching to a photo so "From video" can return. */
  time: number;
  photo?: MediaItem;
};

function formatClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Instagram-style cover: scrub to a frame, or use a photo.
 * The preview matches the crop (and burned-in text) the published cover will use.
 */
export default function VideoCoverControls({
  videoUrl,
  aspect,
  crop,
  caption,
  cover,
  disabled,
  onChange,
  onPickPhoto,
}: {
  videoUrl: string;
  aspect: number;
  crop: SocialCropState;
  caption?: VideoCaption;
  cover?: VideoCoverChoice;
  disabled?: boolean;
  onChange: (next: VideoCoverChoice | undefined) => void;
  onPickPhoto: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [duration, setDuration] = useState(0);
  const time = cover?.mode === 'photo' ? cover.time : cover?.time ?? 0;
  const photoMode = cover?.mode === 'photo' && !!cover.photo;
  const boxW = 72;
  const boxH = Math.max(72, Math.round(boxW / Math.max(0.2, aspect)));

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const onMeta = () => {
      if (Number.isFinite(video.duration)) setDuration(video.duration);
    };
    video.addEventListener('loadedmetadata', onMeta);
    return () => video.removeEventListener('loadedmetadata', onMeta);
  }, [videoUrl]);

  useEffect(() => {
    if (photoMode) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    let cancelled = false;

    const paint = () => {
      if (cancelled || !video.videoWidth || !video.videoHeight) return;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(boxW * dpr);
      canvas.height = Math.round(boxH * dpr);
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const area = crop.croppedAreaPixels;
      const sx = area?.x ?? 0;
      const sy = area?.y ?? 0;
      const sw = area?.width || video.videoWidth;
      const sh = area?.height || video.videoHeight;
      ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
      if (hasCaptionText(caption)) drawVideoCaption(ctx, caption, canvas.width, canvas.height);
    };

    const seek = () => {
      const durationSafe = Number.isFinite(video.duration) ? video.duration : 0;
      const at = Math.min(Math.max(0, time), durationSafe > 0.05 ? durationSafe - 0.05 : 0);
      if (video.readyState >= 1 && Math.abs(video.currentTime - at) < 0.04) {
        paint();
        return;
      }
      video.onseeked = () => {
        if (!cancelled) paint();
      };
      try {
        video.currentTime = at;
      } catch {
        // Metadata not ready yet; loadeddata will retry.
      }
    };

    video.addEventListener('loadeddata', seek);
    if (video.readyState >= 1) seek();
    return () => {
      cancelled = true;
      video.removeEventListener('loadeddata', seek);
    };
  }, [photoMode, time, videoUrl, crop, caption, boxW, boxH]);

  return (
    <div className="space-y-2" data-video-cover-ui>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-gray-500">Cover</span>
        {cover ? (
          <button
            type="button"
            onClick={() => onChange(undefined)}
            disabled={disabled}
            className="text-[11px] font-semibold text-gray-500 hover:text-gray-900 disabled:opacity-50"
          >
            Use first frame
          </button>
        ) : null}
      </div>

      <div className="flex items-center gap-3">
        <div
          className="shrink-0 overflow-hidden rounded-xl bg-gray-900"
          style={{ width: boxW, height: boxH }}
        >
          {photoMode && cover?.photo ? (
            <img
              src={cover.photo.url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <canvas ref={canvasRef} className="h-full w-full" />
          )}
        </div>
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 p-0.5 w-fit">
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                if (cover?.mode === 'photo') onChange({ mode: 'frame', time: cover.time, photo: cover.photo });
              }}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition disabled:opacity-50 ${
                !photoMode ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              From video
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={onPickPhoto}
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition disabled:opacity-50 ${
                photoMode ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Photo
            </button>
          </div>
          {photoMode ? (
            <button
              type="button"
              onClick={onPickPhoto}
              disabled={disabled}
              className="text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50"
            >
              Change photo
            </button>
          ) : (
            <label className="block">
              <span className="sr-only">Cover frame</span>
              <input
                type="range"
                min={0}
                max={Math.max(0.1, duration || 0.1)}
                step={0.1}
                value={Math.min(time, duration || time)}
                disabled={disabled || duration <= 0}
                onChange={(e) =>
                  onChange({
                    mode: 'frame',
                    time: Number(e.target.value),
                    photo: cover?.photo,
                  })
                }
                className="w-full accent-gray-900"
              />
              <span className="mt-0.5 block text-[11px] tabular-nums text-gray-400">
                {formatClock(time)} / {formatClock(duration)}
              </span>
            </label>
          )}
        </div>
      </div>
      <video
        ref={videoRef}
        src={videoUrl}
        muted
        playsInline
        preload="auto"
        crossOrigin="anonymous"
        className="hidden"
      />
      <p className="text-xs text-gray-400">
        Drag to a frame, or use a photo. This replaces the first frame as the cover on Instagram, Facebook, and LinkedIn.
      </p>
    </div>
  );
}
